import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { api } from '../../../core/network/api';
import { IconTrash } from '../../icons';
import Modal from '../modal/modal';

import './styles/deactivate-button.css';

// The delete action of a row, next to its edit action and drawn the same way.
//
// IT DEACTIVATES, IT DOES NOT ERASE. Every `DELETE` Core publishes for these
// rows is a soft delete: the row stays, with its history, and is marked
// inactive. The button says «Desactivar», and the modal says what happens —
// a person who thinks they erased something and finds it later is a person
// who stops trusting the panel.
//
// THE FEATURE KNOWS THE URL, this component does not invent it: `path` comes
// from the feature's queries/paths.js, and `invalidate` names the query keys
// that must be read again. The permission is the caller's <Gate>.
//
// `confirmName` asks for the name to be typed, for the one case where a slip
// costs more than a row (a project takes its repositories with it).

export default function DeactivateButton({
    name,
    noun,
    path,
    invalidate = [],
    consequence,
    confirmName = false,
    className = '',
    onDone,
}) {
    const [open, setOpen] = useState(false);

    return (
        <>
            <button
                type="button"
                className={`deactivate-button ${className}`}
                onClick={() => setOpen(true)}
                aria-label={`Desactivar ${name}`}
                title="Desactivar"
            >
                <IconTrash />
            </button>
            {open && (
                <DeactivateModal
                    name={name}
                    noun={noun}
                    path={path}
                    invalidate={invalidate}
                    consequence={consequence}
                    confirmName={confirmName}
                    onClose={() => setOpen(false)}
                    onDone={onDone}
                />
            )}
        </>
    );
}

function DeactivateModal({ name, noun, path, invalidate, consequence, confirmName, onClose, onDone }) {
    const client = useQueryClient();
    const [typed, setTyped] = useState('');

    const deactivate = useMutation({
        mutationFn: () => api.delete(path).then(r => r.data),
        onSuccess: () => {
            invalidate.forEach(queryKey => client.invalidateQueries({ queryKey }));
        },
    });

    const nameOk = !confirmName || typed.trim() === String(name).trim();

    const handleConfirm = async () => {
        try {
            await deactivate.mutateAsync();
            toast.success(`${capitalize(noun)} desactivado: ${name}`);
            onClose();
            onDone?.();
        } catch (err) {
            toast.error(err.message || `No se pudo desactivar el ${noun}`);
        }
    };

    return (
        <Modal
            open
            title={`Desactivar ${noun}`}
            busy={deactivate.isPending}
            onClose={onClose}
            actions={(
                <>
                    <button
                        type="button"
                        className="deactivate-modal__button"
                        onClick={onClose}
                        disabled={deactivate.isPending}
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        className="deactivate-modal__button deactivate-modal__button--danger"
                        onClick={handleConfirm}
                        disabled={deactivate.isPending || !nameOk}
                        autoFocus={!confirmName}
                    >
                        {deactivate.isPending ? 'Desactivando…' : 'Desactivar'}
                    </button>
                </>
            )}
        >
            <dl className="modal__facts">
                <div>
                    <dt>{capitalize(noun)}</dt>
                    <dd>{name}</dd>
                </div>
            </dl>

            <p className="modal__note">
                {consequence
                    ?? 'Queda inactivo y la fila se conserva, con toda su historia: no se borra nada. Se puede volver a activar editándolo, en «Estado del registro».'}
            </p>

            {confirmName && (
                <label className="modal__field">
                    <span className="modal__field-label">Escribí el nombre para confirmar</span>
                    <input
                        className="modal__input deactivate-modal__input"
                        value={typed}
                        onChange={e => setTyped(e.target.value)}
                        disabled={deactivate.isPending}
                        autoFocus
                        aria-invalid={typed !== '' && !nameOk}
                    />
                </label>
            )}
        </Modal>
    );
}

function capitalize(text) {
    const value = String(text ?? '');
    return value.charAt(0).toUpperCase() + value.slice(1);
}
